import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { DraftWorkItem } from '../../common/types';

/**
 * Service for persisting and retrieving inline-edited draft work item state via the BE.
 *
 * Endpoints (per RFC: https://hello.atlassian.net/wiki/spaces/jai/pages/6805684912):
 *   GET  /gateway/api/ex/jira/${cloudId}/rest/draftactions/conversation/{conversationId}/message/{messageId}
 *   POST /gateway/api/ex/jira/${cloudId}/rest/draftactions/conversation/{conversationId}/message/{messageId}
 */

// The shape of the data field for each individual draft work item.
export type DraftWorkItemWithEditsData = {
	summary: string;
	issueTypeId: string;
	projectIdOrKey: string;
	resolution?: string;
	fields: Record<string, unknown>;
};

// The shape of the draft work item which is either sent or received by the backend.
export type DraftWorkItemWithEdits = {
	invocationId: string;
	actionId?: string;
	data: DraftWorkItemWithEditsData;
};
export type GetDraftWorkItemsResponse = DraftWorkItemWithEdits[];

const buildUrl = (cloudId: string, conversationId: string, messageId: string) =>
	`/gateway/api/ex/jira/${encodeURIComponent(cloudId)}/rest/draftactions/conversation/${encodeURIComponent(conversationId)}/message/${encodeURIComponent(messageId)}`;

// In-memory promise cache so that a prefetch call made before canvas mount
// is reused by getOrFetchDraftWorkItemsWithEdits — avoiding a duplicate network request.
const prefetchCache = new Map<string, Promise<GetDraftWorkItemsResponse | null>>();

// Clears the prefetch cache. Exposed for testing only.
export const clearDraftActionsCache = (): void => prefetchCache.clear();

const getCacheKey = (conversationId: string, messageId: string): string =>
	`${conversationId}:${messageId}`;

export const mapStoreItemToDraftWorkItemWithEdits = (
	storeItem: DraftWorkItem,
): DraftWorkItemWithEdits => {
	return {
		invocationId: storeItem.invocationId,
		data: { ...storeItem },
	};
};

const fetchDraftActions = async (
	cloudId: string,
	conversationId: string,
	messageId: string,
	sendAifcAnalyticsEvent?: SendAnalyticsEvent,
): Promise<GetDraftWorkItemsResponse | null> => {
	try {
		const response = await fetch(buildUrl(cloudId, conversationId, messageId), {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
		});
		if (!response.ok) {
			sendAifcAnalyticsEvent?.({
				action: 'failed',
				actionSubject: 'jiraAifcGetDraftWorkItems',
				attributes: { statusCode: response.status },
				eventType: 'operational',
			});
			return null;
		}

		const data = await response.json();
		sendAifcAnalyticsEvent?.({
			action: 'success',
			actionSubject: 'jiraAifcGetDraftWorkItems',
			attributes: { numActions: data?.length ?? 0, statusCode: response.status },
			eventType: 'operational',
		});
		return data;
	} catch (error) {
		sendAifcAnalyticsEvent?.({
			action: 'errored',
			actionSubject: 'jiraAifcGetDraftWorkItems',
			attributes: { error: String(error) },
			eventType: 'operational',
		});
	}
	return null;
};

/**
 * Fetches draft work items for the given conversation and message, caching the promise so that
 * repeated calls (e.g. an early call for prefetching and a later call to consume the result)
 * never make more than one network request per key.
 */
export const getOrFetchDraftWorkItemsWithEdits = (
	cloudId: string,
	conversationId: string,
	messageId: string,
	sendAnalyticsEvent?: SendAnalyticsEvent,
): Promise<GetDraftWorkItemsResponse | null> => {
	const key = getCacheKey(conversationId, messageId);
	if (!prefetchCache.has(key)) {
		prefetchCache.set(
			key,
			fetchDraftActions(cloudId, conversationId, messageId, sendAnalyticsEvent),
		);
	}
	return prefetchCache.get(key)!;
};

/**
 * Performs a POST request to save a draft action and its updated changes to the BE state.
 */
export const saveDraftWorkItem = async (
	cloudId: string,
	conversationId: string,
	messageId: string,
	draftAction: DraftWorkItemWithEdits,
	sendAifcAnalyticsEvent?: SendAnalyticsEvent,
	experienceTracker?: ExperienceTrackerAPI,
): Promise<void> => {
	// Unique id so that rapid concurrent field-edit saves do not clobber one another.
	const experienceId = `${conversationId}:${messageId}:${draftAction.invocationId}`;

	if (fg('jira-aifc-track-ufo-experiences')) {
		experienceTracker?.start({
			id: experienceId,
			name: ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM,
		});
	}

	try {
		const response = await fetch(buildUrl(cloudId, conversationId, messageId), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify({ draftAction }),
		});

		sendAifcAnalyticsEvent?.({
			action: response.ok ? 'success' : 'failed',
			actionSubject: 'jiraAifcSaveDraftWorkItem',
			attributes: { statusCode: response.status },
			eventType: 'operational',
		});

		if (fg('jira-aifc-track-ufo-experiences')) {
			if (response.ok) {
				experienceTracker?.succeed({ name: ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM });
			} else {
				experienceTracker?.fail({
					name: ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM,
					error: new Error(`Failed to save draft work item: ${response.status}`),
				});
			}
		}
	} catch (error) {
		sendAifcAnalyticsEvent?.({
			action: 'errored',
			actionSubject: 'jiraAifcSaveDraftWorkItem',
			attributes: { error: error instanceof Error ? error.message : String(error) },
			eventType: 'operational',
		});

		if (fg('jira-aifc-track-ufo-experiences')) {
			experienceTracker?.fail({
				name: ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM,
				error: error instanceof Error ? error : new Error(String(error)),
			});
		}
	}
};

/**
 * Performs a POST request to resolve a draft work item with the given resolution in the monolith.
 */
export const resolveDraftWorkItemInMonolith = async (
	cloudId: string,
	conversationId: string,
	messageId: string,
	draftAction: DraftWorkItemWithEdits,
	resolution: MessageActionResolutions,
	sendAifcAnalyticsEvent?: SendAnalyticsEvent,
	experienceTracker?: ExperienceTrackerAPI,
): Promise<void> => {
	// Unique id so that rapid concurrent field-edit saves do not clobber one another.
	const experienceId = `${conversationId}:${messageId}:${draftAction.invocationId}`;

	if (fg('jira-aifc-track-ufo-experiences')) {
		experienceTracker?.start({
			id: experienceId,
			name: ExperienceName.JIRA_RESOLVE_DRAFT_WORK_ITEM,
		});
	}

	try {
		const response = await fetch(buildUrl(cloudId, conversationId, messageId), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify({
				draftAction: {
					...draftAction,
					data: {
						...draftAction.data,
						resolution,
					},
				},
			}),
		});

		sendAifcAnalyticsEvent?.({
			action: response.ok ? 'success' : 'failed',
			actionSubject: 'jiraAifcResolveDraftWorkItem',
			attributes: { statusCode: response.status, resolution },
			eventType: 'operational',
		});

		if (fg('jira-aifc-track-ufo-experiences')) {
			if (response.ok) {
				experienceTracker?.succeed({ name: ExperienceName.JIRA_RESOLVE_DRAFT_WORK_ITEM });
			} else {
				experienceTracker?.fail({
					name: ExperienceName.JIRA_RESOLVE_DRAFT_WORK_ITEM,
					error: new Error(`Failed to resolve draft work item: ${response.status}`),
				});
			}
		}
	} catch (error) {
		sendAifcAnalyticsEvent?.({
			action: 'errored',
			actionSubject: 'jiraAifcResolveDraftWorkItem',
			attributes: { error: error instanceof Error ? error.message : String(error), resolution },
			eventType: 'operational',
		});

		if (fg('jira-aifc-track-ufo-experiences')) {
			experienceTracker?.fail({
				name: ExperienceName.JIRA_RESOLVE_DRAFT_WORK_ITEM,
				error: error instanceof Error ? error : new Error(String(error)),
			});
		}
	}
};
